import { useEffect, useState } from 'react';
import { auditApi } from '../lib/api';
import type { AuditLog } from '../lib/types';
import Skeleton from './Skeleton';

interface AuditDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditLog: AuditLog;
}

function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function formatFieldName(field: string): string {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return dateString;
  }
}

export default function AuditDetailModal({ isOpen, onClose, auditLog }: AuditDetailModalProps) {
  const [detailedLog, setDetailedLog] = useState<AuditLog | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && auditLog) {
      setLoading(true);
      auditApi
        .getDetail(auditLog.PK, auditLog.SK)
        .then(setDetailedLog)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isOpen, auditLog]);

  if (!isOpen) return null;

  const log = detailedLog || auditLog;

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'UPDATE':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'DELETE':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'SOFT_DELETE':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />

      <div
        className="relative bg-white/95 backdrop-blur-md w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl border border-white/20 transform transition-all flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white/50">
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Audit Log Details</h3>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-0.5">
              Ref: {auditLog?.PK?.split('#').pop() || 'N/A'} • {auditLog?.SK?.split('#').pop() || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
          ) : (
            <>
              {/* Summary Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Entity Type</p>
                  <p className="text-sm font-semibold text-gray-900">{log.entityType}</p>
                </div>
                <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Entity ID</p>
                  <p className="text-sm font-semibold text-blue-600 font-mono">{log.entityId}</p>
                </div>
                <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Action</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getActionColor(log.action)}`}>
                    {log.action}
                  </span>
                </div>
                <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Timestamp</p>
                  <p className="text-sm font-medium text-gray-700">{formatDate(log.timestamp || log.created_at)}</p>
                </div>
              </div>

              {/* User Attribution */}
              <div className="flex items-center gap-3 p-4 bg-blue-50/30 rounded-xl border border-blue-100/50">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Performed By</p>
                  <p className="text-sm font-semibold text-gray-900">{log.userName || log.userId || 'System Entity'}</p>
                </div>
              </div>

              {/* Data Blocks */}
              <div className="space-y-6">
                {log.newValues && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Object Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-green-50/20 border border-green-100 rounded-2xl">
                      {Object.entries(log.newValues).map(([key, value]) => {
                        if (['PK', 'SK', 'GSI1PK', 'GSI1SK', 'type'].includes(key)) return null;
                        return (
                          <div key={key} className="flex flex-col py-2 border-b border-green-100/50 last:border-0 md:odd:border-r md:odd:pr-4 md:even:pl-4">
                            <span className="text-[10px] font-bold text-green-600/70 uppercase tracking-wider">{formatFieldName(key)}</span>
                            <span className="text-sm font-medium text-gray-900 break-words">{formatValue(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {log.action === 'DELETE' && log.oldValues && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full" />
                      Purged Object State
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-red-50/20 border border-red-100 rounded-2xl">
                      {Object.entries(log.oldValues).map(([key, value]) => {
                        if (['PK', 'SK', 'GSI1PK', 'GSI1SK', 'type'].includes(key)) return null;
                        return (
                          <div key={key} className="flex flex-col py-2 border-b border-red-100/50 last:border-0 md:odd:border-r md:odd:pr-4 md:even:pl-4">
                            <span className="text-[10px] font-bold text-red-600/70 uppercase tracking-wider">{formatFieldName(key)}</span>
                            <span className="text-sm font-medium text-gray-900 line-through opacity-70 break-words">{formatValue(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {log.action === 'UPDATE' && log.changes && log.changes.length > 0 && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      Field Transitions
                    </h4>
                    <div className="space-y-4">
                      {log.changes.map((change, index) => (
                        <div key={index} className="overflow-hidden rounded-2xl border border-blue-100 shadow-sm transition-all hover:shadow-md">
                          <div className="bg-blue-50/50 px-4 py-2 border-b border-blue-100">
                            <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">{formatFieldName(change.field)}</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-blue-50">
                            <div className="p-4 bg-white">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">From</p>
                              <div className="text-sm text-gray-600 bg-red-50/30 p-2 rounded-lg border border-red-50/50 font-medium">
                                {formatValue(change.oldValue)}
                              </div>
                            </div>
                            <div className="p-4 bg-white">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">To</p>
                              <div className="text-sm text-blue-700 bg-green-50/30 p-2 rounded-lg border border-green-50/50 font-bold">
                                {formatValue(change.newValue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl shadow-sm hover:bg-gray-50 transition-all active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
